import { defineStore } from "pinia";
import { useArtworkStore } from "./artwork";
import { observeElements, SelectorObject, hasActiveAnimation, getAnimationDuration, isPopoverElement } from "@/utils/dom";
import router from "@/router";
import { useEventTracking } from "@/hooks/useEventTracking";
import { ServerResponse } from "@/types/request";
import { useRouteStore } from "./route";
import { debounce } from "lodash";
import { nextTick } from "vue";
import { sleep } from "@/utils";

const delay = (delay: number) => new Promise((resolve) => setTimeout(resolve, delay));

export const useNewUserGuide = defineStore('newUserGuide', {
    state: () => ({
        model: '欧美风格角色V1',
        prompt: '一位皮肤白皙、棕色头发的年轻女孩，头戴草帽，身穿蓝色工装裤和白衬衫。她左手上举，向着镜头打招呼，右手抱着装满新鲜蔬菜的黄色篮子。她脸上洋溢着笑容，眼睛看向镜头。女孩左侧站着一只浅棕色和白色相间的奶牛，奶牛抬头看向女孩。背景是田园风光，一座红色谷仓和小风车坐落在山坡上，周围是茂密的绿植和木栅栏。天空湛蓝，飘着白云，还有一道柔和的彩虹。',
        isShowNewUserGuide: false,
        placement: 'top',
        nodeList: [] as SelectorObject[],
        showActionBar: true,
        isNeedShow: false,
        modelSelectModalIsShow: false,
        curStep: 1,
        isMockGenerate: false,
        isProgress: false, // 是否已经在显示过程中
        isShowMockCreationVersionList: false,
        // 是否正在虚拟生成中
        isVirtualGenerate: false,
        isShowNewUserGuideForB: false,
        curStepForB: 1,
        isInGuideForB: false,
        nodeListForB: [] as SelectorObject[],
        guideForBModelId: 0,
        // 是否生成过图片，如果未生成过，且为B组用户，且处于一键成稿页面，则首次消耗算力为0
        hasGenerateImage: false,
        /**
         * B组是否需要显示生图教程
         */
        isShowGenerateImageTutorial: false,
        /**
         * 是否能免费生图，用于新手B组用户首次生图
         */
        isAbleFreeGenerateImage: false,
        /**
         * 是否显示随机提示弹窗
         */
        hasShowRandomPromptModal: false,
        /**
         * 新用户引导是否已经初始化完成
         */
        isNewUserGuideInitialized: false
    }),
    actions: {
        // 初始化 resize 事件监听
        initializeResizeListener() {
            this.handleResize = debounce(this.handleResize, 300);
            window.addEventListener('resize', this.handleResize);
        },
        // 移除 resize 事件监听
        removeResizeListener() {
            window.removeEventListener('resize', this.handleResize);
        },
        // 处理屏幕大小变化，重新定位元素
        handleResize() {
            if (this.isShowNewUserGuide) {
                // 对于resize事件，使用强化的位置更新
                this.updateGuidePositionRobust(3);
            }
        },
        // 更新引导位置
        updateGuidePosition() {
            if (this.nodeList.length) {
                // 调用 observeElements 更新节点信息
                observeElements(this.nodeList, this.curStep).then((results) => {
                    this.nodeList = results;
                    setTimeout(() => {
                        this.showNewUserGuide();
                    }, 100);
                });
            }
        },
        /**
         * 延迟更新引导位置，用于处理动画元素
         * @param delay 延迟时间(ms)，默认300ms
         */
        updateGuidePositionWithDelay(delay: number = 300) {
            if (this.nodeList.length) {
                // 等待动画完成后再更新位置
                setTimeout(async () => {
                    try {
                        const results = await observeElements(this.nodeList, this.curStep);
                        this.nodeList = results;
                        // 再次延迟一帧确保渲染完成
                        await nextTick();
                        setTimeout(() => {
                            this.showNewUserGuide();
                        }, 50);
                    } catch (error) {
                        console.warn('更新引导位置失败:', error);
                        // 降级到普通更新方式
                        this.updateGuidePosition();
                    }
                }, delay);
            }
        },
        /**
         * 智能更新引导位置，自动检测是否需要等待动画
         */
        updateGuidePositionSmart() {
            if (!this.nodeList.length) return;

            // 检查是否有元素包含动画
            let hasAnimation = false;
            let maxAnimationDuration = 0;

            this.nodeList.forEach(({ selector }) => {
                const elements = document.querySelectorAll(selector);
                elements.forEach(element => {
                    if (hasActiveAnimation(element)) {
                        hasAnimation = true;
                        const duration = getAnimationDuration(element);
                        maxAnimationDuration = Math.max(maxAnimationDuration, duration);
                    }
                });
            });

            if (hasAnimation) {
                // 如果有动画，等待动画完成再更新
                const delay = Math.min(maxAnimationDuration + 100, 1000); // 最多等待1秒
                console.log(`检测到动画元素，延迟 ${delay}ms 后更新引导位置`);
                this.updateGuidePositionWithDelay(delay);
            } else {
                // 如果没有动画，直接更新
                this.updateGuidePosition();
            }
        },
        /**
         * 强化的位置更新，带重试机制
         */
        updateGuidePositionRobust(maxRetries: number = 2) {
            if (!this.nodeList.length) return;

            const attemptUpdate = async (attempt: number = 0): Promise<void> => {
                try {
                    console.log(`尝试更新引导位置，第 ${attempt + 1} 次`);

                    // 等待所有元素可见并获取稳定位置
                    const results = await observeElements(this.nodeList, this.curStep);
                    const prevPositions = this.nodeList.map(item => item.info?.rect);

                    this.nodeList = this.fineTuning(results);

                    // 验证位置是否发生了显著变化
                    const hasSignificantChange = this.nodeList.some((item, index) => {
                        const oldRect = prevPositions[index];
                        const newRect = item.info?.rect;

                        if (!oldRect || !newRect) return true;

                        return Math.abs(oldRect.x - newRect.x) > 2 ||
                            Math.abs(oldRect.y - newRect.y) > 2;
                    });

                    if (hasSignificantChange && attempt < maxRetries) {
                        console.log(`位置变化较大，${500}ms 后重试`);
                        setTimeout(() => attemptUpdate(attempt + 1), 500);
                        return;
                    }

                    // 最终更新
                    await nextTick();
                    setTimeout(() => {
                        this.showNewUserGuide();
                    }, 100);

                } catch (error) {
                    console.warn(`位置更新失败，尝试次数: ${attempt + 1}`, error);

                    if (attempt < maxRetries) {
                        setTimeout(() => attemptUpdate(attempt + 1), 300);
                    } else {
                        // 最后的降级方案
                        this.updateGuidePosition();
                    }
                }
            };

            attemptUpdate();
        },
        fineTuning(result: any[]) {
            return result.map(hole => {
                const rect = hole?.info?.rect || {};
                const handleClick = hole?.handleClick || false;
                const adjustment = hole?.holeAdjustment || {};

                // 获取元素的实际DOM引用以进行更精确的计算
                const element = hole?.info?.element;
                let dynamicAdjustment = { ...adjustment };

                // 如果元素存在，进行动态调整
                if (element) {
                    const computedStyle = getComputedStyle(element);
                    const parentElement = element.parentElement;
                    const isPopover = isPopoverElement(element);

                    // Popover元素特殊处理
                    if (isPopover) {
                        console.log(`🎯 处理Popover元素: ${hole.selector}`);

                        // 检查Popover的placement
                        const placement = element.getAttribute('data-placement') ||
                            element.closest('[data-placement]')?.getAttribute('data-placement') ||
                            'auto';

                        console.log(`Popover placement: ${placement}`);

                        // 根据placement调整位置
                        switch (placement) {
                            case 'top':
                            case 'topLeft':
                            case 'topRight':
                                dynamicAdjustment.offsetY = (dynamicAdjustment.offsetY ?? 0) - 5;
                                break;
                            case 'bottom':
                            case 'bottomLeft':
                            case 'bottomRight':
                                dynamicAdjustment.offsetY = (dynamicAdjustment.offsetY ?? 0) + 5;
                                break;
                            case 'left':
                            case 'leftTop':
                            case 'leftBottom':
                                dynamicAdjustment.offsetX = (dynamicAdjustment.offsetX ?? 0) - 5;
                                break;
                            case 'right':
                            case 'rightTop':
                            case 'rightBottom':
                                dynamicAdjustment.offsetX = (dynamicAdjustment.offsetX ?? 0) + 5;
                                break;
                        }

                        // 检查是否在portal中渲染
                        const isInPortal = !!element.closest('body > div:not(#app)');
                        if (isInPortal) {
                            console.log('检测到Portal渲染的Popover');
                            // Portal渲染的元素可能需要额外的位置调整
                            dynamicAdjustment.offsetX = (dynamicAdjustment.offsetX ?? 0);
                            dynamicAdjustment.offsetY = (dynamicAdjustment.offsetY ?? 0);
                        }

                        // 检查Popover容器的z-index
                        const zIndex = parseInt(computedStyle.zIndex) || 0;
                        if (zIndex > 1000) {
                            console.log(`高z-index Popover (${zIndex})，可能需要特殊处理`);
                        }
                    }

                    // 检查是否有transform影响
                    if (computedStyle.transform && computedStyle.transform !== 'none') {
                        console.log(`🔄 检测到transform: ${computedStyle.transform}`);
                        // 对于有transform的元素，可能需要额外的偏移调整
                    }

                    // 检查是否在滚动容器内
                    if (parentElement) {
                        const parentStyle = getComputedStyle(parentElement);
                        if (parentStyle.overflow === 'auto' || parentStyle.overflow === 'scroll') {
                            // 考虑滚动容器的影响
                            const scrollTop = parentElement.scrollTop;
                            const scrollLeft = parentElement.scrollLeft;
                            if (scrollTop > 0 || scrollLeft > 0) {
                                console.log(`📜 检测到滚动容器偏移: scrollTop=${scrollTop}, scrollLeft=${scrollLeft}`);
                            }
                        }
                    }

                    // 特殊选择器的预设调整
                    if (hole.selector?.includes('switch-btn')) {
                        // 对于开关按钮，可能需要特殊处理
                        dynamicAdjustment = {
                            ...dynamicAdjustment,
                            offsetY: dynamicAdjustment.offsetY ?? -15,
                            offsetX: dynamicAdjustment.offsetX ?? -5,
                        };
                    }

                    // 为popover相关的选择器提供预设调整
                    if (hole.selector?.includes('popover') || hole.selector?.includes('tooltip')) {
                        dynamicAdjustment = {
                            ...dynamicAdjustment,
                            offsetY: dynamicAdjustment.offsetY ?? -8,
                            offsetX: dynamicAdjustment.offsetX ?? -8,
                            paddingWidth: dynamicAdjustment.paddingWidth ?? 16,
                            paddingHeight: dynamicAdjustment.paddingHeight ?? 16,
                        };
                    }
                }

                // 使用调整后的配置，如果没有配置则使用默认逻辑
                const defaultOffsetX = -12;
                const defaultOffsetY = -12;
                const defaultPaddingWidth = handleClick ? 15 : 24;
                const defaultPaddingHeight = handleClick ? 15 : 24;

                const offsetX = dynamicAdjustment.offsetX !== undefined ? dynamicAdjustment.offsetX : defaultOffsetX;
                const offsetY = dynamicAdjustment.offsetY !== undefined ? dynamicAdjustment.offsetY : defaultOffsetY;
                const paddingWidth = dynamicAdjustment.paddingWidth !== undefined ? dynamicAdjustment.paddingWidth : defaultPaddingWidth;
                const paddingHeight = dynamicAdjustment.paddingHeight !== undefined ? dynamicAdjustment.paddingHeight : defaultPaddingHeight;

                // 计算最终位置和尺寸
                const top = rect.y + offsetY;
                const left = rect.x + offsetX;

                // 如果设置了固定尺寸，则使用固定尺寸，否则使用原始尺寸加上调整量
                const width = dynamicAdjustment.fixedSize?.width || (rect.width || 0) + paddingWidth;
                const height = dynamicAdjustment.fixedSize?.height || (rect.height || 0) + paddingHeight;

                const r = {
                    ...hole,
                    info: {
                        ...hole?.info,
                        rect: {
                            ...rect,
                            top,
                            left,
                            width,
                            height,
                            y: top,
                            x: left,
                            // 如果配置了圆角半径，则使用配置的值
                            radius: dynamicAdjustment.radius !== undefined ? dynamicAdjustment.radius : rect.radius
                        }
                    }
                }

                return r;
            });
        },
        step1() {
            this.curStep = 1;

            this.placement = 'bottom';
            const selectorsObj: SelectorObject[] = [
                {
                    selector: '.ceration-module-scroll-container .creation-module-item:first-child',
                    popverTips: true,
                    handleClick: async () => {
                        this.isShowNewUserGuide = false;
                        router.push({
                            path: '/workspace/explore'
                        })
                        await delay(2000);

                        useEventTracking({
                            event: 'click',
                            eventValue: {
                                explore_onboarding: '完成引导-1'
                            }
                        });

                        this.step2();
                    },
                    handleMouseEnter: () => {
                        document.querySelector('.ceration-module-scroll-container .creation-module-item:first-child')?.classList.add('active');
                    },
                    handleMounseLeave: () => {
                        document.querySelector('.ceration-module-scroll-container .creation-module-item:first-child')?.classList.remove('active');
                    }
                },
            ];

            observeElements(selectorsObj, 1).then((results) => {
                results = this.fineTuning(results)
                this.isShowNewUserGuide = true;

                useEventTracking({
                    event: 'click',
                    eventValue: {
                        explore_onboarding: '进入引导'
                    }
                });

                this.nodeList = results;
                setTimeout(() => {
                    this.showNewUserGuide()
                }, 150)
            });
        },
        step2() {
            this.curStep = 2;
            this.placement = 'top';
            const artworkStore = useArtworkStore();

            artworkStore.explore.params.prompt = this.prompt;
            const selectorsObj: SelectorObject[] = [
                {
                    selector: '.panel-footer-btns button:last-child',
                    popverTips: true,
                    name: 'generateBtn',
                    handleClick: async () => {
                        useEventTracking({
                            event: 'click',
                            eventValue: {
                                explore_onboarding: '完成引导-2'
                            }
                        });

                        this.isShowNewUserGuide = false;
                        this.isVirtualGenerate = true;
                        let progress = 0;
                        this.isShowMockCreationVersionList = true;


                        let timer = setInterval(async () => {
                            if (progress >= 100) {
                                clearInterval(timer);
                                await delay(100);
                                this.isVirtualGenerate = false;
                                this.isMockGenerate = true;
                                await nextTick()
                                artworkStore.startGenerate(1);
                                return;
                            }
                            artworkStore.explore.result = {
                                data: null,
                                msg: '正在快速生成中',
                                progress: progress += 5,
                                code: 2,
                            }
                        }, 100)

                    }
                },
                {
                    selector: '.workspace-params-drawer .top-panel .model-panel-item',
                    placement: 'right',
                    popverTips: true,
                    name: 'styleTips',
                },
                /** 正向描述词 */
                {
                    selector: '.workspace-params-drawer .base-positive-descriptor',
                    placement: 'right',
                    popverTips: true,
                    name: 'promptTips',
                }
            ];

            observeElements(selectorsObj, 3).then((results) => {
                results = this.fineTuning(results)
                this.isShowNewUserGuide = true;
                this.nodeList = results;
                setTimeout(() => {
                    this.showNewUserGuide()
                }, 150)
            });
        },
        step3() {
            this.curStep = 3;
            this.placement = 'left';

            const element = document.querySelector('.generate-content .image-hover-item:last-child');
            if (element) {
                element.classList.add('active');
                const selectorsObj: SelectorObject[] = [
                    {
                        selector: '.generate-content .image-hover-item:last-child .download-and-publish',
                        popverTips: true,
                        handleClick: () => {
                            this.isShowNewUserGuide = false;
                            this.eventTrack();
                            element.classList.remove('active');
                        }
                    }
                ];

                observeElements(selectorsObj, 1).then((results) => {
                    results = this.fineTuning(results)
                    this.isShowNewUserGuide = true;
                    this.nodeList = results;
                    setTimeout(() => {
                        this.showNewUserGuide()
                    }, 150)
                });
            }
        },
        step1ForB() {
            this.curStepForB = 1;
            this.placement = 'top';
            this.isInGuideForB = true;
            this.isAbleFreeGenerateImage = true;
            const artworkStore = useArtworkStore();
            const selectorsObj: SelectorObject[] = [

                /** 正向描述词 */
                {
                    selector: '.propmt-textarea-container textarea',
                    placement: 'right',
                    pointerEvents: ['.propmt-textarea-container .ant-input,.step-actions button'],
                    name: 'promptTips',
                    popverTips: true,
                    holeAdjustment: {
                        offsetX: 1,
                        offsetY: 0,
                        paddingWidth: 0,
                        // fixedSize: {
                        //     width: 314,  
                        //     height: 144  
                        // },
                        radius: 12
                    }
                }
            ];

            // 清理之前的状态
            this.nodeListForB = [];

            observeElements(selectorsObj, selectorsObj.length).then(async (results) => {
                results = this.fineTuning(results)

                // 减少延时，提供更快的响应
                setTimeout(async () => {
                    this.isShowNewUserGuideForB = true;
                    this.nodeListForB = results;

                    useEventTracking({
                        event: 'click',
                        eventValue: {
                            explore_onboarding: '进入引导',
                            explore_onboarding_model: artworkStore.artworkState.modelList?.[0]?.modelName,
                            explore_onboarding_prompt: artworkStore.explore.params?.prompt
                        }
                    });


                    await nextTick();
                    setTimeout(() => {
                        // 再次验证位置准确性
                        this.updateGuidePositionRobust(2);
                    }, 100);
                }, 20)
            });
        },
        step2ForB() {
            this.curStepForB = 2;
            this.isAbleFreeGenerateImage = true;
            const selectorsObj: SelectorObject[] = [
                {
                    selector: '.panel-footer-btns button:last-child',
                    popverTips: true,
                    placement: 'rightBottom',
                    pointerEvents: ['.progress-indicator .progress-indicator__dot', '.closeBtn', '.step-actions__btn--prev', '.step-actions button'],
                    name: 'generateBtn',
                    handleClick: async () => {
                        const artworkStore = useArtworkStore();
                        this.isMockGenerate = true;
                        await nextTick()
                        artworkStore.startGenerate(1);
                        this.isShowNewUserGuideForB = false;
                        this.isVirtualGenerate = true;
                        this.hasGenerateImage = true;
                        this.isInGuideForB = false;
                        useEventTracking({
                            event: 'click',
                            eventValue: {
                                explore_onboarding: '完成引导'
                            }
                        });

                    }
                },

            ];

            // 清理之前的状态
            this.nodeListForB = [];

            observeElements(selectorsObj, selectorsObj.length).then((results) => {
                results = this.fineTuning(results)

                // 立即显示，提供更快的响应
                this.isShowNewUserGuideForB = true;
                this.nodeListForB = results;
            });
        },
        step3ForB(result: ServerResponse<any>) {
            const artworkStore = useArtworkStore();
            result.code = 3;
            this.isShowMockCreationVersionList = false;
            artworkStore.explore.result = result as any;
            this.isMockGenerate = false;
            this.isNeedShow = false;

        },

        // step1ForB() {
        //     this.curStepForB = 1;
        //     this.isInGuideForB = true;

        //     this.placement = 'top';
        //     const artworkStore = useArtworkStore();
        //     this.isAbleFreeGenerateImage = true;


        //     const selectorsObj: SelectorObject[] = [
        //         {
        //             selector: '.panel-footer-btns button:last-child',
        //             popverTips: true,
        //             name: 'generateBtn',
        //             handleClick: async () => {
        //                 useEventTracking({
        //                     event: 'click',
        //                     eventValue: {
        //                         explore_onboarding: '完成引导-1'
        //                     }
        //                 });

        //                 this.isShowNewUserGuide = false;
        //                 this.isVirtualGenerate = true;
        //                 let progress = 0;
        //                 this.isShowMockCreationVersionList = true;

        //                 let timer = setInterval(async () => {
        //                     if (progress >= 100) {
        //                         clearInterval(timer);
        //                         await delay(100);
        //                         this.isVirtualGenerate = false;
        //                         this.isMockGenerate = true;
        //                         await nextTick()
        //                         artworkStore.startGenerate(1);
        //                         this.isInGuideForB = false;
        //                         return;
        //                     }
        //                     artworkStore.explore.result = {
        //                         data: null,
        //                         msg: '正在快速生成中',
        //                         progress: progress += 5,
        //                         code: 2,
        //                     }
        //                 }, 100)

        //             }
        //         },
        //         {
        //             selector: '.workspace-params-drawer .top-panel .model-panel-item',
        //             placement: 'right',
        //             popverTips: true,
        //             name: 'styleTips',
        //         },
        //         /** 正向描述词 */
        //         {
        //             selector: '.workspace-params-drawer .base-positive-descriptor',
        //             placement: 'right',
        //             popverTips: true,
        //             name: 'promptTips',
        //         }
        //     ];

        //     observeElements(selectorsObj, 3).then((results) => {
        //         results = this.fineTuning(results)
        //         this.isShowNewUserGuide = true;
        //         this.nodeList = results;

        //         useEventTracking({
        //             event: 'click',
        //             eventValue: {
        //               explore_onboarding: '进入引导',
        //               explore_onboarding_model: artworkStore.artworkState.modelList[0].modelName,
        //               explore_onboarding_prompt: artworkStore.explore.params.prompt
        //             }
        //         });

        //         setTimeout(() => {
        //             this.showNewUserGuide()
        //         }, 150)
        //     });
        // },
        // step2ForB(result: ServerResponse<any>) {

        //     const artworkStore = useArtworkStore();
        //     result.code = 3;
        //     this.isShowMockCreationVersionList = false;
        //     artworkStore.explore.result = result as any;
        //     this.isMockGenerate = false;
        //     this.isNeedShow = false;

        //     setTimeout(() => {
        //         this.curStepForB = 2;

        //         this.placement = 'left';

        //         const element = document.querySelector('.generate-content .image-hover-item:last-child');
        //         if (element) {
        //             element.classList.add('active');
        //             const selectorsObj: SelectorObject[] = [
        //                 {
        //                     selector: '.generate-content .image-hover-item:last-child .download-and-publish',
        //                     popverTips: true,
        //                     handleClick: () => {
        //                         this.isShowNewUserGuide = false;
        //                         this.eventTrack();
        //                         element.classList.remove('active');
        //                     }
        //                 }
        //             ];

        //             observeElements(selectorsObj, selectorsObj.length).then((results) => {
        //                 results = this.fineTuning(results)
        //                 this.isShowNewUserGuide = true;
        //                 this.nodeList = results;
        //                 setTimeout(() => {
        //                     this.showNewUserGuide()
        //                 }, 150)
        //             });
        //         }
        //     }, 1000)
        // },
        showNewUserGuide: () => { },
        eventTrack() {
            useEventTracking({
                event: 'click',
                eventValue: {
                    explore_onboarding: this.exploreOnboarding
                }
            });
        },
        showStep4(result: ServerResponse<any>) {
            const artworkStore = useArtworkStore();
            result.code = 3;
            this.isShowMockCreationVersionList = false;
            artworkStore.explore.result = result as any;
            this.isMockGenerate = false;
            this.isNeedShow = false;
            setTimeout(() => {
                this.step3();
            }, 1000)
        },
        showCurGuide() {
            const routeStore = useRouteStore();
            const routeName = routeStore.currentRouteName;
            if (!this.isProgress && this.isNeedShow) {
                if (routeName === 'workbench' && this.curStep === 1) {
                    this.step1();
                } else if (routeName === 'explore') {
                    if (this.curStep === 2) this.step2();
                    if (this.curStep === 3) this.step3();
                }
            }
            this.removeResizeListener()
            this.initializeResizeListener()
        },
        /**
         * 调试方法：检查元素位置信息
         */
        debugElementPositions() {
            console.group('🔍 引导元素位置调试信息');

            this.nodeList.forEach((node, index) => {
                const element = document.querySelector(node.selector);
                if (element) {
                    const rect = element.getBoundingClientRect();
                    const computedStyle = getComputedStyle(element);
                    const isPopover = isPopoverElement(element);

                    console.log(`📍 元素 ${index + 1}:`, {
                        selector: node.selector,
                        isPopover: isPopover,
                        position: {
                            x: rect.x,
                            y: rect.y,
                            width: rect.width,
                            height: rect.height
                        },
                        visibility: {
                            visible: rect.width > 0 && rect.height > 0,
                            display: computedStyle.display,
                            visibility: computedStyle.visibility,
                            opacity: computedStyle.opacity,
                            zIndex: computedStyle.zIndex
                        },
                        hasAnimation: hasActiveAnimation(element),
                        animationDuration: getAnimationDuration(element),
                        transform: computedStyle.transform,
                        transition: computedStyle.transition,
                        animation: computedStyle.animation,
                        position_style: computedStyle.position,
                        scrollTop: document.documentElement.scrollTop,
                        scrollLeft: document.documentElement.scrollLeft,
                        holeAdjustment: node.holeAdjustment,
                        // Popover特殊信息
                        ...(isPopover && {
                            popoverInfo: {
                                container: element.closest('.ant-popover, .popover, .tooltip, .dropdown'),
                                placement: element.getAttribute('data-placement') || element.closest('[data-placement]')?.getAttribute('data-placement'),
                                portal: !!element.closest('body > div:not(#app)'), // 检查是否在portal中
                            }
                        })
                    });
                } else {
                    console.warn(`❌ 元素未找到: ${node.selector}`);
                }
            });

            console.groupEnd();
        },
        /**
         * 专门处理Popover元素的位置更新
         */
        async updatePopoverGuidePosition() {
            if (!this.nodeList.length) return;

            console.log('🎯 开始Popover专用位置更新');

            // 检查是否有Popover元素
            const hasPopoverElements = this.nodeList.some(({ selector }) => {
                const element = document.querySelector(selector);
                return element && isPopoverElement(element);
            });

            if (!hasPopoverElements) {
                console.log('未检测到Popover元素，使用常规更新');
                this.updateGuidePositionRobust();
                return;
            }

            try {
                // 对于有Popover的情况，需要特殊处理
                console.log('检测到Popover元素，等待稳定显示...');

                // 等待更长时间让Popover完全渲染
                await sleep(300);

                // 多次尝试获取稳定位置
                let attempts = 0;
                const maxAttempts = 5;

                while (attempts < maxAttempts) {
                    try {
                        const results = await observeElements(this.nodeList, this.curStep);

                        // 验证Popover元素是否正确显示
                        const allVisible = results.every(item => {
                            const rect = item.info?.rect;
                            return rect && rect.width > 0 && rect.height > 0;
                        });

                        if (allVisible) {
                            this.nodeList = this.fineTuning(results);
                            await nextTick();

                            setTimeout(() => {
                                this.showNewUserGuide();
                            }, 100);

                            console.log(`✅ Popover位置更新成功，尝试次数: ${attempts + 1}`);
                            return;
                        }

                    } catch (error) {
                        console.warn(`Popover位置更新失败，尝试 ${attempts + 1}/${maxAttempts}:`, error);
                    }

                    attempts++;
                    if (attempts < maxAttempts) {
                        await sleep(200);
                    }
                }

                // 如果所有尝试都失败，使用降级方案
                console.warn('Popover位置更新多次失败，使用降级方案');
                this.updateGuidePositionRobust(2);

            } catch (error) {
                console.error('Popover位置更新出错:', error);
                this.updateGuidePosition();
            }
        },
        /**
         * 智能选择更新方法（根据是否有Popover元素）
         */
        smartUpdateGuide() {
            // 检查当前引导是否包含Popover元素
            const hasPopover = this.nodeList.some(({ selector }) => {
                const element = document.querySelector(selector);
                return element && isPopoverElement(element);
            });

            if (hasPopover) {
                console.log('🎯 检测到Popover元素，使用专用更新方法');
                this.updatePopoverGuidePosition();
            } else {
                console.log('📍 使用常规强化更新方法');
                this.updateGuidePositionRobust();
            }
        },
    },
    getters: {
        exploreOnboarding({ curStep, isInGuideForB, curStepForB }) {
            if (isInGuideForB) {
                return curStepForB === 2 ? '完成引导' : `跳过引导-${curStepForB}`;
            }
            return curStep === 3 ? '完成引导' : `跳过引导-${curStep}`;
        }
    },
    persist: {
        key: 'pinoNewUserGuideStore',
        storage: window.localStorage,
        paths: ['curStep', 'isNeedShow']
    },
});